import AuditInfo from 'components/audit/AuditInfo';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import { useDmsTeamSelectState } from 'components/dms/DmsTeamSelect';
import CustomFieldDisplay from 'components/forms/inputs/custom-fields/CustomFieldDisplay';
import { groupFieldsByHeaders, useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import GridItemList, { filterRowList, Row } from 'components/GridItemList';
import { RecordLink } from 'components/record-chip/RecordLink';
import { RECORD_GRID_CLASS } from 'components/record-header/RecordHeader';
import cloneDeep from 'lodash/cloneDeep';
import { getLinkChips } from 'pages/matters/util';
import React, { useContext, useMemo } from 'react';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { Team } from 'types/teams';
import { getClassifierText, t } from 'utils/getTranslations';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import CustomFieldCards from '../../offices/CustomFieldCards';
import { TeamSection } from '../form-sections/TeamSection';
import { useDmsTeamFieldGate } from '../form-sections/useTeamForm';
import { teamDtoToForm } from '../teamFormMapping';

interface Props {
    team: Team;
    classifiers: Record<string, ClassifierData>;
    allClassifiers: ClassifierData[];
    /** The new design's cards, each editing its own fields in place. */
    isNew?: boolean;
}

// A detail-row link: `grid-item-list-row .entity-link` (index.css) colours it as a link at rest.
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

const linkifyList = <T extends { id: number; name: string }>(items: T[] | null | undefined, base: string) => {
    if (!items || items.length === 0) return '-';
    return (
        <div className="flex flex-col">
            {items.map((item) => (
                <RecordLink className={LINK_CLASS} key={item.id} to={`/${base}/${item.id}`} label={item.name} />
            ))}
        </div>
    );
};

type LinkedItem = { id: number; name: string; isActive?: boolean };

/** The team's read rows by name, `null` where the team's type or the flag leaves a row out. */
const teamRows = (team: Team, classifiers: Record<string, ClassifierData>, isDmsTeamAdIntegrationEnabled: boolean) => {
    const businessAreaLabels = (team.teamBusinessAreas ?? []).flatMap((area) => {
        const label = getClassifierText(classifiers['BUSINESS_AREA_TYPE'], area.businessAreaTypeCode);
        return label ? [label] : [];
    });
    return {
        type: {
            label: t('teams.team-type'),
            content: team.teamTypeCode ? getClassifierText(classifiers['TEAM_TYPE'], team.teamTypeCode) || '-' : '-',
        },
        name: { label: t('name'), content: team.name || '-' },
        initials: { label: t('initials'), content: team.abbreviation || '-' },
        isParent: { label: t('forms.is-parent'), content: team.isParent ? t('yes') : t('no') },
        parentTeam: {
            label: t('teams.parent-team'),
            content: team.parentTeam?.id ? (
                <RecordLink className={LINK_CLASS} to={`/team/${team.parentTeam.id}`} label={team.parentTeam.name} />
            ) : (
                '-'
            ),
        },
        practiceAreas:
            team.teamTypeCode === 'PRACTICE_GROUP'
                ? { label: t('teams.practice-areas'), content: linkifyList(team.teamPracticeAreas, 'practice-area') }
                : null,
        businessAreas:
            team.teamTypeCode === 'SECTOR_GROUP'
                ? {
                      label: t('teams.business-areas'),
                      content: businessAreaLabels.length > 0 ? businessAreaLabels.join(', ') : '-',
                  }
                : null,
        offices: { label: t('teams.offices'), content: linkifyList(team.teamOffices, 'office') },
        email: {
            label: t('teams.team-email'),
            content: team.emailAddress ? (
                <a className={LINK_CLASS} href={`mailto:${team.emailAddress}`}>
                    {team.emailAddress}
                </a>
            ) : (
                '-'
            ),
        },
        active: { label: t('active'), content: team.isActive ? t('yes') : t('no') },
        dmsTeam:
            team.dmsTeamId && isDmsTeamAdIntegrationEnabled
                ? { label: t('teams.azure-ad-group'), content: team.dmsTeamName || team.dmsTeamId }
                : null,
    } satisfies Record<string, Row | null>;
};

const GeneralView: React.FC<Props> = ({ team, classifiers, allClassifiers, isNew = false }) => {
    const isDmsTeamAdIntegrationEnabled = useIsFeatureEnabled(FeatureFlag.DMS_TEAM_AD_INTEGRATION_ENABLED);

    const rows = useMemo(
        () => teamRows(team, classifiers, isDmsTeamAdIntegrationEnabled),
        [team, classifiers, isDmsTeamAdIntegrationEnabled]
    );
    const detailRows: Row[] = useMemo(
        () =>
            filterRowList([
                rows.type,
                rows.name,
                rows.initials,
                rows.isParent,
                rows.parentTeam,
                rows.practiceAreas,
                rows.businessAreas,
                rows.offices,
                rows.email,
                rows.active,
                rows.dmsTeam,
            ]),
        [rows]
    );

    const customFieldGroups = useMemo(
        () => groupFieldsByHeaders(cloneDeep((team.customFields as CustomField[]) ?? [])),
        [team.customFields]
    );

    const draftActions = useContext(DraftActionsContext);

    if (isNew) {
        return <TeamGeneralCards team={team} rows={rows} classifiers={classifiers} allClassifiers={allClassifiers} />;
    }

    return (
        <div className="flex flex-col gap-6">
            <GridItemList
                titleId="matters.overview"
                data={withMissing(detailRows, draftActions?.draft?.missingFields)}
                key="team-general"
            />
            {draftActions ? <DraftSharedWith /> : null}
            {customFieldGroups.length > 0 ? (
                <div className="flex flex-col gap-6">
                    {customFieldGroups.map(({ id, section, fields, shouldTranslate }) => (
                        <GridItemList
                            key={`${id}_${section}`}
                            titleId={section}
                            shouldTranslate={shouldTranslate}
                            data={fields.map((field) => ({
                                label: field.label ? (field.shouldTranslate ? t(field.label) : field.label) : '',
                                content: (
                                    <CustomFieldDisplay key={field.id} field={field} classifiers={allClassifiers} />
                                ),
                            }))}
                        />
                    ))}
                </div>
            ) : null}
            <div className="px-2">
                <AuditInfo
                    createdAt={team.createdAt}
                    createdByEmployee={team.createdByEmployee}
                    lastModifiedAt={team.lastModifiedAt ?? undefined}
                    changedByEmployee={team.changedByEmployee}
                />
            </div>
        </div>
    );
};

/**
 * The new design's General tab: one card per topic, each editing its own fields in place. A card lists the team
 * form's fields for it in the form's order, each drawn as its field and where the form shows it; a row no field
 * edits is `readOnly`.
 */
const TeamGeneralCards = ({
    team,
    rows,
    classifiers,
    allClassifiers,
}: {
    team: Team;
    rows: ReturnType<typeof teamRows>;
    classifiers: Record<string, ClassifierData>;
    allClassifiers: ClassifierData[];
}) => {
    const { customFields, customFieldsFiltered } = useCustomFields({
        entityType: EntityType.TEAM,
        fields: team.customFields,
    });
    // The form has its DMS group field for a user with the right, behind the flag, once OneDrive is known to be on.
    const { isDmsTeamAdIntegrationEnabled, hasDmsTeamField } = useDmsTeamFieldGate();
    const { isShown: isDmsSelectShown } = useDmsTeamSelectState({ skip: !hasDmsTeamField });
    const showsDmsField = hasDmsTeamField && isDmsSelectShown;
    // `TeamPage` holds its loader until that answer is in: the row's place depends on it, and a row that moves, or
    // comes late, would shift the card. An answer that fails leaves the form without the field, and the row as text.
    const showsDmsRow = isDmsTeamAdIntegrationEnabled && (!!team.dmsTeamId || showsDmsField);
    // The multi-selects' values as the form holds them, so the chips come in the form's order.
    const form = teamDtoToForm(team, classifiers, {});
    const teamCardRows: Row[] = filterRowList([
        rows.type,
        showsDmsRow
            ? {
                  label: t('teams.azure-ad-group'),
                  // The field names a group that has lost its name; without the field the row shows its id.
                  content:
                      team.dmsTeamName ||
                      (showsDmsField && team.dmsTeamId
                          ? t('teams.unknown-dms-team', { id: team.dmsTeamId })
                          : team.dmsTeamId) ||
                      '-',
                  readOnly: !showsDmsField,
              }
            : null,
        rows.name,
        rows.initials,
        rows.isParent,
        team.isParent ? null : rows.parentTeam,
        // A parent group picks its practice areas. A child's row stays as it reads: its field lists the parent's.
        rows.practiceAreas && team.isParent
            ? { ...rows.practiceAreas, chips: getLinkChips('practice-area', team.teamPracticeAreas ?? []) }
            : rows.practiceAreas,
        rows.businessAreas
            ? {
                  ...rows.businessAreas,
                  chips: form.teamBusinessAreas.map(({ label, value }) => ({ key: value, label })),
              }
            : null,
        rows.active,
    ]);
    const contactRows: Row[] = [
        {
            ...rows.offices,
            chips: getLinkChips(
                'office',
                form.teamOffices.map((office: LinkedItem) => ({
                    id: office.id,
                    name: `${office.name ?? ''}${officeInactiveSuffix(office) ?? ''}`,
                }))
            ),
        },
        rows.email,
    ];
    // On a draft, the values it was saved without read as missing, and whom it is shared with shows.
    const draftActions = useContext(DraftActionsContext);
    const missingFields = draftActions?.draft?.missingFields;
    return (
        <div className={RECORD_GRID_CLASS}>
            <div className="flex min-w-0 flex-col gap-(--card-gap)">
                <TeamSection id="team" titleId="teams.team" team={team} rows={teamCardRows}>
                    {(edit) => (
                        <GridItemList
                            isNew
                            titleId="teams.team"
                            data={withMissing(teamCardRows, missingFields)}
                            button={edit}
                        />
                    )}
                </TeamSection>
                {customFieldsFiltered.length > 0 ? (
                    <TeamSection id="custom-fields" titleId="custom-fields.title" team={team}>
                        {(edit) => <CustomFieldCards fields={customFields} classifiers={allClassifiers} edit={edit} />}
                    </TeamSection>
                ) : null}
                <AuditInfo
                    createdAt={team.createdAt}
                    createdByEmployee={team.createdByEmployee}
                    lastModifiedAt={team.lastModifiedAt ?? undefined}
                    changedByEmployee={team.changedByEmployee}
                />
            </div>
            <div className="flex min-w-0 flex-col gap-(--card-gap)">
                {draftActions ? <DraftSharedWith /> : null}
                <TeamSection id="contact" titleId="teams.contact-and-offices" team={team}>
                    {(edit) => (
                        <GridItemList
                            isNew
                            titleId="teams.contact-and-offices"
                            data={withMissing(contactRows, missingFields)}
                            button={edit}
                        />
                    )}
                </TeamSection>
            </div>
        </div>
    );
};

export default GeneralView;
